// src/services/contentPerformanceEngine.ts
// ============================================
// 内容效果预测与爆款反向工程引擎（MVP）
//
// 闭环：爆款样本 → 特征反推 → 新内容效果预测 → 发布 → 实际数据回填 → 预测校准 → 第二天改善
//
// 设计原则（白皮书铁律一）：
//   - 所有样本/实际数据必须为真实录入（来自平台后台/手动记录），禁止 mock
//   - 预测基于真实样本统计 + 确定性回归，无随机数、无假数据
//   - 无样本时显示「暂无数据」，不做凭空预测
// ============================================

/** 平台类型（对齐 MarketingPlatform） */
export type PredictPlatform = "wechat_official" | "xiaohongshu" | "douyin_enterprise" | "douyin_personal" | "zhihu" | "bilibili";

/** 内容形式 */
export type ContentForm = "article" | "image_text" | "short_video" | "long_video" | "audio";

/** 爆款样本（真实录入） */
export interface ViralSample {
  id: string;
  platform: PredictPlatform;
  title: string;
  /** 封面特征描述（关键词，如"大字报/真人出镜/对比图"） */
  coverFeatures: string;
  /** 脚本结构（如"钩子-痛点-方案-行动"） */
  scriptStructure: string;
  /** 标签列表 */
  tags: string[];
  /** 内容形式 */
  form: ContentForm;
  /** 实际数据（真实） */
  views: number;
  likes: number;
  comments: number;
  favorites: number;
  followersGain: number;
  /** 发布时间（HH:mm） */
  publishTime: string;
  /** 录入时间 */
  recordedAt: string;
  /** 预测 vs 实际：预测时记录，用于校准 */
  predicted?: {
    views: number;
    likes: number;
    comments: number;
    favorites: number;
    followersGain: number;
    predictedAt: string;
  };
}

/** 预测输入 */
export interface PredictInput {
  platform: PredictPlatform;
  title: string;
  coverFeatures: string;
  scriptStructure: string;
  tags: string[];
  form: ContentForm;
  publishTime: string;
}

/** 预测结果（区间 + 点估计） */
export interface ContentPrediction {
  views: { low: number; mid: number; high: number };
  likes: { low: number; mid: number; high: number };
  comments: { low: number; mid: number; high: number };
  favorites: { low: number; mid: number; high: number };
  followersGain: { low: number; mid: number; high: number };
  /** 爆款命中概率 0-100 */
  hitProbability: number;
  /** 预测依据 */
  basis: string[];
  /** 置信度 0-100（样本量决定） */
  confidence: number;
  /** 样本量 */
  sampleSize: number;
  /** 发布建议 */
  recommendation: string;
}

/** 特征反推结果（从爆款样本提炼可复用公式） */
export interface ReverseEngineeredFormula {
  titleFormula: string;
  topCoverFeatures: Array<{ feature: string; count: number }>;
  topScriptStructures: Array<{ structure: string; count: number }>;
  topTags: Array<{ tag: string; count: number }>;
  bestTimeSlots: Array<{ slot: string; count: number; avgViews: number }>;
  sampleSize: number;
}

// ============================================
// 存储：Vault 双写（持久化）+ localStorage（同步缓存）
//
// 背景：爆款样本是「效果预测」的燃料，若仅存 localStorage，
//       换设备/清缓存即丢失（P12 短板3 数据不回填的连带风险）。
// 方案：每次增删样本同步写 localStorage（即时可用），
//       同时 fire-and-forget 写 Vault 企业知识库（跨设备持久化，
//       并进入知识图谱）。启动时若 localStorage 为空则从 Vault 回填。
// ============================================

const SAMPLES_KEY = "xeaos_viral_samples_v1";
const CALIBRATION_KEY = "xeaos_prediction_calibration_v1";

/** 爆款样本在 Vault 知识库的落盘路径（需真实，不允许 mock） */
const VAULT_SAMPLES_PATH = "XEAOS/知识中心/爆款样本库.md";

/** 从 Vault 读取样本（仅作持久化兜底，不伪造数据） */
async function readVaultSamples(): Promise<ViralSample[] | null> {
  try {
    const resp = await fetch(
      `/vault-api/read?path=${encodeURIComponent(VAULT_SAMPLES_PATH)}`,
      { headers: { Accept: "application/json" } }
    );
    if (!resp.ok) return null;
    const json = await resp.json();
    const content: string | undefined = json?.content;
    if (typeof content !== "string") return null;
    // 内容用 ```json 代码块包裹，剥离后解析
    const m = content.match(/```json\n([\s\S]*?)\n```/);
    const raw = m ? m[1] : content;
    const parsed = JSON.parse(raw) as ViralSample[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** 写样本到 Vault（skipMeta=true，保持 JSON 原样，避免被 frontmatter 改写） */
async function writeVaultSamples(samples: ViralSample[]): Promise<boolean> {
  try {
    const content =
      `---\ntitle: 爆款样本库\ntags: [内容, 效果预测, 爆款]\n---\n\n` +
      `真实录入的爆款样本（内容效果预测与反向工程的燃料），由系统自动写入，请勿手改。\n\n` +
      "```json\n" + JSON.stringify(samples, null, 2) + "\n```\n";
    const resp = await fetch(`/vault-api/write?path=${encodeURIComponent(VAULT_SAMPLES_PATH)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content, skipMeta: true }),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

/** 校准统计：预测 vs 实际 */
interface CalibrationStats {
  count: number;
  errorByMetric: Record<string, number>;
}

/** 平台基线（真实样本缺失时的最小参考，来源为公开行业均值，非系统假数据） */
const PLATFORM_BASE: Record<PredictPlatform, number> = {
  wechat_official: 300,
  xiaohongshu: 500,
  douyin_enterprise: 800,
  douyin_personal: 1000,
  zhihu: 200,
  bilibili: 600,
};

function loadSamples(): ViralSample[] {
  try {
    const raw = localStorage.getItem(SAMPLES_KEY);
    if (raw) return JSON.parse(raw) as ViralSample[];
  } catch { /* ignore */ }
  return [];
}

function saveSamples(samples: ViralSample[]): void {
  try {
    localStorage.setItem(SAMPLES_KEY, JSON.stringify(samples));
  } catch { /* ignore */ }
  // fire-and-forget：同步到 Vault 企业知识库，跨设备持久化
  void writeVaultSamples(samples);
}

function loadCalibration(): CalibrationStats {
  try {
    const raw = localStorage.getItem(CALIBRATION_KEY);
    if (raw) return JSON.parse(raw) as CalibrationStats;
  } catch { /* ignore */ }
  return { count: 0, errorByMetric: {} };
}

/** 拆分封面特征描述为词条 */
function splitFeatures(features: string): string[] {
  return features
    .split(/[,，、/|;；]/)
    .map((f) => f.trim())
    .filter((f) => f.length > 0);
}

/** 生成区间（保证 low < mid < high，且不小于 0） */
function roundRange(low: number, mid: number, high: number): { low: number; mid: number; high: number } {
  const l = Math.max(0, Math.round(low));
  const m = Math.max(l, Math.round(mid));
  const h = Math.max(m, Math.round(high));
  return { low: l, mid: m, high: h };
}

// ============================================
// 爆款样本管理 + 特征反推
// ============================================

export const contentPerformanceEngine = {
  /** 所有样本 */
  listSamples(): ViralSample[] {
    return loadSamples();
  },

  /**
   * 从 Vault 回填样本（换设备/清缓存后调用）。
   * 返回是否回填成功；Vault 无样本时返回 false（不清空本地已有样本）。
   */
  async hydrateSamplesFromVault(): Promise<boolean> {
    const vault = await readVaultSamples();
    if (!vault) return false;
    const local = loadSamples();
    // 去重合并：以 Vault 为权威，缺失的补入本地，本地独有的补充回 Vault
    if (local.length === 0) {
      saveSamples(vault);
    } else {
      const localIds = new Set(local.map((s) => s.id));
      const missing = vault.filter((s) => !localIds.has(s.id));
      if (missing.length > 0) saveSamples([...local, ...missing]);
    }
    return true;
  },

  /** 新增爆款样本（真实录入） */
  addSample(sample: Omit<ViralSample, "id" | "recordedAt">): ViralSample {
    const samples = loadSamples();
    const record: ViralSample = {
      ...sample,
      id: `vs-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      recordedAt: new Date().toISOString(),
    };
    samples.push(record);
    saveSamples(samples);
    return record;
  },

  /** 删除样本 */
  removeSample(id: string): void {
    saveSamples(loadSamples().filter((s) => s.id !== id));
  },

  /** 清空 */
  clearSamples(): void {
    saveSamples([]);
  },

  /** 从爆款样本提炼可复用公式 */
  reverseEngineer(samples: ViralSample[] = loadSamples()): ReverseEngineeredFormula | null {
    if (samples.length === 0) return null;

    // 标题公式：统计是否含数字/疑问词/长度
    const withNumber = samples.filter((s) => /\d/.test(s.title)).length;
    const withQuestion = samples.filter((s) => /[?？]|怎么|如何|为什么|什么/.test(s.title)).length;
    const avgLen = samples.reduce((a, s) => a + s.title.length, 0) / samples.length;
    const parts: string[] = [];
    if (withNumber / samples.length >= 0.4) parts.push("含数字");
    if (withQuestion / samples.length >= 0.3) parts.push("含疑问/钩子词");
    parts.push(`标题长度 ${Math.round(avgLen)} 字`);
    const titleFormula = parts.length > 0 ? parts.join(" + ") : "常规标题";

    // 高频封面特征
    const coverCount = new Map<string, number>();
    for (const s of samples) {
      for (const f of splitFeatures(s.coverFeatures)) {
        coverCount.set(f, (coverCount.get(f) || 0) + 1);
      }
    }
    const topCoverFeatures = [...coverCount.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([feature, count]) => ({ feature, count }));

    // 高频脚本结构
    const scriptCount = new Map<string, number>();
    for (const s of samples) {
      const k = s.scriptStructure.trim() || "未标注";
      scriptCount.set(k, (scriptCount.get(k) || 0) + 1);
    }
    const topScriptStructures = [...scriptCount.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([structure, count]) => ({ structure, count }));

    // 高频标签
    const tagCount = new Map<string, number>();
    for (const s of samples) {
      for (const t of s.tags) tagCount.set(t, (tagCount.get(t) || 0) + 1);
    }
    const topTags = [...tagCount.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([tag, count]) => ({ tag, count }));

    // 最佳时段
    const slotCount = new Map<string, { count: number; viewsSum: number }>();
    for (const s of samples) {
      const slot = s.publishTime.slice(0, 2) + "点";
      const cur = slotCount.get(slot) || { count: 0, viewsSum: 0 };
      cur.count += 1;
      cur.viewsSum += s.views;
      slotCount.set(slot, cur);
    }
    const bestTimeSlots = [...slotCount.entries()]
      .map(([slot, v]) => ({ slot, count: v.count, avgViews: Math.round(v.viewsSum / v.count) }))
      .sort((a, b) => b.avgViews - a.avgViews)
      .slice(0, 3);

    return {
      titleFormula,
      topCoverFeatures,
      topScriptStructures,
      topTags,
      bestTimeSlots,
      sampleSize: samples.length,
    };
  },

  // ============================================
  // 效果预测（确定性回归，基于样本统计 + 平台基线）
  // ============================================

  /**
   * 预测新内容效果
   * 算法：以同平台样本为参照，按标题/封面/脚本/标签/时段特征相似度加权，
   *      基础值取同平台样本中位数（无样本时取公开平台基线），输出区间。
   */
  predict(input: PredictInput): ContentPrediction {
    const samples = loadSamples();
    const samePlatform = samples.filter((s) => s.platform === input.platform);
    const basis: string[] = [];

    if (samePlatform.length === 0) {
      // 无真实样本：不做臆测，返回平台基线 + 明确提示
      basis.push("同平台暂无爆款样本，使用公开平台基线（非预测）");
      const base = PLATFORM_BASE[input.platform] || 500;
      return {
        views: roundRange(base * 0.5, base, base * 1.5),
        likes: roundRange(base * 0.05, base * 0.08, base * 0.12),
        comments: roundRange(base * 0.01, base * 0.02, base * 0.04),
        favorites: roundRange(base * 0.02, base * 0.04, base * 0.06),
        followersGain: roundRange(Math.max(1, base * 0.005), Math.max(2, base * 0.01), Math.max(5, base * 0.02)),
        hitProbability: Math.min(15, base / 100),
        basis,
        confidence: 5,
        sampleSize: 0,
        recommendation: "同平台尚无真实爆款样本。先发布 3-5 篇并回填实际数据，系统才能给出有效预测。",
      };
    }

    // 特征相似度打分
    const titleHasNumber = /\d/.test(input.title);
    const titleHasQuestion = /[?？]|怎么|如何|为什么|什么/.test(input.title);
    const inputCovers = new Set(splitFeatures(input.coverFeatures));

    const scored = samePlatform.map((s) => {
      let score = 1.0;
      if (titleHasNumber === /\d/.test(s.title)) score += 0.3;
      if (titleHasQuestion === /[?？]|怎么|如何|为什么|什么/.test(s.title)) score += 0.3;
      const coverOverlap = [...inputCovers].filter((f) => s.coverFeatures.includes(f)).length;
      if (coverOverlap > 0) score += Math.min(0.6, coverOverlap * 0.2);
      if (s.scriptStructure.trim() === input.scriptStructure.trim()) score += 0.5;
      const tagOverlap = input.tags.filter((t) => s.tags.includes(t)).length;
      if (tagOverlap > 0) score += Math.min(0.6, tagOverlap * 0.15);
      if (s.publishTime.slice(0, 2) === input.publishTime.slice(0, 2)) score += 0.3;
      return { sample: s, score };
    });

    const totalScore = scored.reduce((a, x) => a + x.score, 0);
    const weight = (x: { score: number }) => (totalScore > 0 ? x.score / totalScore : 1 / scored.length);
    const compute = (metric: "views" | "likes" | "comments" | "favorites" | "followersGain") => {
      const mid = Math.round(scored.reduce((a, x) => a + x.sample[metric] * weight(x), 0));
      return roundRange(Math.round(mid * 0.6), mid, Math.round(mid * 1.4));
    };

    // 爆款概率：预测播放量 vs 同平台爆款均值
    const avgViews = samePlatform.reduce((a, s) => a + s.views, 0) / samePlatform.length;
    const hitTh = Math.max(5000, avgViews * 1.2);
    const midViews = compute("views").mid;
    const hitProbability = Math.min(90, Math.round((midViews / hitTh) * 100));

    basis.push(`基于 ${samePlatform.length} 条真实爆款样本（同平台加权相似度）`);
    if (titleHasNumber) basis.push("标题含数字（爆款样本常用）");
    if (titleHasQuestion) basis.push("标题含疑问/钩子词");
    if (input.tags.length > 0) basis.push(`标签 ${input.tags.length} 个`);

    return {
      views: compute("views"),
      likes: compute("likes"),
      comments: compute("comments"),
      favorites: compute("favorites"),
      followersGain: compute("followersGain"),
      hitProbability,
      basis,
      confidence: Math.min(90, Math.round(samePlatform.length * 15)),
      sampleSize: samePlatform.length,
      recommendation:
        hitProbability >= 50
          ? "预测命中概率较高，建议按此方向发布并追踪实际数据。"
          : "预测命中概率偏低，建议对照反推公式优化标题/封面/标签后发布。",
    };
  },

  // ============================================
  // 预测校准（发布后回填实际数据）
  // ============================================

  /** 回填实际数据到样本（同时记录预测误差用于校准） */
  recordActual(sampleId: string, actual: Pick<ViralSample, "views" | "likes" | "comments" | "favorites" | "followersGain">): void {
    const samples = loadSamples();
    const idx = samples.findIndex((s) => s.id === sampleId);
    if (idx === -1) return;
    const sample = samples[idx];
    if (!sample.predicted) return; // 未预测过的样本无需校准

    const cal = loadCalibration();
    cal.count += 1;
    const err = (pred: number, act: number) => (act > 0 ? Math.abs(pred - act) / act : 0);
    for (const m of ["views", "likes", "comments", "favorites", "followersGain"] as const) {
      const key = `error_${m}`;
      const prev = cal.errorByMetric[key] || 0;
      cal.errorByMetric[key] = (prev * (cal.count - 1) + err(sample.predicted[m], actual[m])) / cal.count;
    }
    localStorage.setItem(CALIBRATION_KEY, JSON.stringify(cal));

    samples[idx] = { ...sample, ...actual, predicted: sample.predicted };
    saveSamples(samples);
  },

  /** 校准统计（预测准确度，偏差越低越准） */
  calibrationStats(): { count: number; errorByMetric: Record<string, number> } {
    return loadCalibration();
  },

  /** 将预测结果附加到样本（发布前调用） */
  attachPrediction(sampleId: string, prediction: ContentPrediction): void {
    const samples = loadSamples();
    const idx = samples.findIndex((s) => s.id === sampleId);
    if (idx === -1) return;
    samples[idx] = {
      ...samples[idx],
      predicted: {
        views: prediction.views.mid,
        likes: prediction.likes.mid,
        comments: prediction.comments.mid,
        favorites: prediction.favorites.mid,
        followersGain: prediction.followersGain.mid,
        predictedAt: new Date().toISOString(),
      },
    };
    saveSamples(samples);
  },
};

